import React, {useCallback} from 'react';
import {useCommand} from 'hooks/useCommand';
import {DialogInfo, useDialogState} from 'components/Dialogs';
import {RequirementStatus, UserRole} from 'types';
import {useUserContext} from 'authentication/UserContext';
import {useRequirementsTranslation} from './useRequirementsTranslation';
import {WorkFlowDialogHandler} from '../RequirementDetailsPage/WorkFlowDialog/WorkFlowDialogHandler';
import {RequirementStatusType} from '../RequirementDetailsPage/RequirementDetailsPage';
import {RequirementFromDetailsPage} from '../RequirementDetailsPage/requirementDetailsPage.types';

interface UseSentToApprovalCommandParams {
	requirements: RequirementFromDetailsPage[];
	vexClusterMatch: boolean;
}

export const useSentToApprovalCommand = ({
	requirements,
	vexClusterMatch,
}: UseSentToApprovalCommandParams) => {
	const {t} = useRequirementsTranslation();
	const {isVexSupport} = useUserContext();

	const [dialogType, setDialogType] = React.useState<RequirementStatusType>(
		RequirementStatusType.None,
	);
	const [dialogState, showDialog, hideDialog]: DialogInfo = useDialogState();

	const setAndShowDialog = useCallback(
		(statusType: RequirementStatusType) => {
			setDialogType(statusType);
			showDialog();
		},
		[showDialog],
	);

	console.debug(`>> vexClusterMatch is ${vexClusterMatch}`);

	useCommand(
		{
			key: 'sentToApproval',
			priority: 2,
			text: t('Complete'),
			iconProps: {iconName: 'ReleaseGate'},
			hidden: isVexSupport,
			disabled:
				requirements.length === 0 ||
				requirements.some(
					r =>
						r.status === RequirementStatus.Approval ||
						r.status === RequirementStatus.Interim ||
						r.status === RequirementStatus.InterimCompleted ||
						r.status === RequirementStatus.Final,
				) ||
				!vexClusterMatch,
			roles: [UserRole.Vex, UserRole.SystemAdministrator],
			subMenuProps: {
				items: [
					{
						key: 'Approval',
						text: t('Approval'),
						onClick() {
							setAndShowDialog(RequirementStatusType.SendToApproval);
						},
					},
					{
						key: 'CompleteWithoutApproval',
						text: t('WorkflowDialog.CompleteWithoutApproval'),
						onClick() {
							setAndShowDialog(RequirementStatusType.CompleteWithoutApproval);
						},
					},
				],
			},
		},
		[requirements, vexClusterMatch],
	);

	const workflowDialog =
		requirements.length > 0 ? (
			<WorkFlowDialogHandler
				dialogState={dialogState}
				dialogType={dialogType}
				hideDialog={hideDialog}
				requirements={requirements}
			/>
		) : null;

	return {workflowDialog};
};
